import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import styles from './index.module.scss'
import TCStatusBadge, { normalizeTcStatus } from '../t&c-status-badge'
import useMutation from '../../hooks/useMutation'
import sendClientTermsAndConditionMutation from '../../graphql/mutations/sendClientTermsAndCondition'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import notify from '../../utilities/notify'
import dayjs from '../../utilities/dayjs'

const SENT_EVENT_TYPE = 'SENT'
const DATE_DISPLAY_FORMAT = 'MMM D, YYYY'
const DATE_KEY_FORMAT = 'YYYY-MM-DD'

const formatDate = (date) => {
  if (!date) return 'N/A'
  const parsed = dayjs(date)
  return parsed.isValid() ? parsed.format(DATE_DISPLAY_FORMAT) : 'N/A'
}

function hasValue (value) {
  if (value == null) return false
  const text = String(value).trim()
  return text !== '' && text !== '—' && text !== '-'
}

function buildTriggerLine (assignment) {
  if (!assignment) return null

  const parts = [
    assignment.triggerText,
    assignment.deliveryMethodText,
    assignment.expirationText
  ].filter(hasValue)

  return parts.length > 0 ? parts.join(' • ') : null
}

function resolveCurrentAssignment (client) {
  const current = client?.currentClientTermsAndCondition
  if (current) return current

  return client?.clientTermsAndConditions?.find((item) => item.isCurrent) || null
}

function resolveDownloadUrl (item, client, isCurrent = false) {
  if (isCurrent) {
    return item?.downloadUrl || client?.currentClientTermsAndCondition?.downloadUrl || null
  }

  return item?.downloadUrl || null
}

function resolveLastActionDate (item) {
  // signed -> sent/link -> assigned.
  return item?.signedAt || item?.sentAt || item?.assignedAt || null
}

function toDateKey (date) {
  const parsed = dayjs(date)
  return parsed.isValid() ? parsed.format(DATE_KEY_FORMAT) : null
}

function resolveSentDates (item) {
  const uniqueByDay = new Map()

  ;(item?.events || [])
    .filter((event) => String(event?.eventType || '').toUpperCase() === SENT_EVENT_TYPE && event?.createdAt)
    .forEach((event) => {
      const key = toDateKey(event.createdAt)
      if (!key || uniqueByDay.has(key)) return
      uniqueByDay.set(key, event.createdAt)
    })

  return Array.from(uniqueByDay.values()).sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf())
}

function mapAssignmentRow (item, client, { isCurrent = false } = {}) {
  if (!item) return null

  return {
    id: item.id,
    name: item.agreementTitle || 'N/A',
    status: item.displayStatus || item.status,
    contactEmail: client?.email || null,
    contactPhone: client?.prettyPrimaryPhone || null,
    trigger: buildTriggerLine(item),
    lastActionDate: resolveLastActionDate(item),
    sentDates: resolveSentDates(item),
    documentUrl: resolveDownloadUrl(item, client, isCurrent)
  }
}

function AgreementRow ({ agreement, showDivider }) {
  return (
    <div className={cn(styles.agreementRow, { [styles.rowDivider]: showDivider })}>
      <div className={styles.agreementLeft}>
        <i className={cn('material-icons', styles.docIcon)}>description</i>
        <div>
          <div className={styles.agreementTitleRow}>
            <span className={styles.agreementName}>{agreement.name}</span>
            <TCStatusBadge status={agreement.status} />
          </div>
          <div className={styles.agreementMeta}>
            Contact Email : {agreement.contactEmail || 'N/A'}
            {agreement.contactPhone && <> &nbsp; Phone : {agreement.contactPhone}</>}
          </div>
          <div className={styles.agreementMeta}>
            Trigger : {agreement.trigger || 'N/A'}
          </div>
        </div>
      </div>

      <div className={styles.agreementRight}>
        <div className={styles.dateColumn}>
          <div className={styles.lastActionDate}>
            Last Action Date : {formatDate(agreement.lastActionDate)}
          </div>
          {agreement.sentDates?.length > 0 && (
            <div className={styles.sentDates}>
              Sent Dates : {agreement.sentDates.map(formatDate).join(', ')}
            </div>
          )}
        </div>
        {agreement.documentUrl && (
          <a
            className={styles.downloadBtn}
            href={agreement.documentUrl}
            target='_blank'
            rel='noopener noreferrer'
            title='Download'
            aria-label='Download'>
            <i className='material-icons'>file_download</i>
          </a>
        )}
      </div>
    </div>
  )
}

AgreementRow.propTypes = {
  agreement: PropTypes.object.isRequired,
  showDivider: PropTypes.bool
}

export default function TCAgreementDisplay ({ client, refetchClient }) {
  const [showHistory, setShowHistory] = useState(false)

  const {
    mutate: sendClientTermsAndCondition,
    isLoading: isSendingLink
  } = useMutation(
    sendClientTermsAndConditionMutation,
    {
      onSuccess () {
        notify('success', 'T&C link sent to customer')
        refetchClient().catch((error) => {
          captureErrorAndNotify(error, 'Failed to refresh T&C agreement')
        })
      },
      onError (error) {
        captureErrorAndNotify(error, 'Failed to send T&C link')
      }
    }
  )

  const assignment = resolveCurrentAssignment(client)

  const history = useMemo(() => {
    const currentId = assignment?.id

    return (client?.termsAndConditionHistory || [])
      .filter((item) => !item.isCurrent && item.id !== currentId)
      .slice()
      .sort((a, b) => {
        const aDate = dayjs(resolveLastActionDate(a) || 0).valueOf()
        const bDate = dayjs(resolveLastActionDate(b) || 0).valueOf()
        return bDate - aDate
      })
      .map((item) => mapAssignmentRow(item, client))
  }, [client, assignment])

  const currentAgreement = useMemo(
    () => mapAssignmentRow(assignment, client, { isCurrent: true }),
    [assignment, client]
  )

  const isSigned = normalizeTcStatus(assignment?.displayStatus) === 'signed'
  const hasNewerPublishedVersion = Boolean(assignment?.hasNewerPublishedVersion)
  const isSendDisabled = isSendingLink || !currentAgreement || (isSigned && !hasNewerPublishedVersion)

  return (
    <div className={cn('dis-panel', styles.tcAgreementPanel)}>
      <div className={styles.panelHeader}>
        <h4>T&amp;C Agreement Information</h4>
        <button
          type='button'
          className={styles.sendLinkBtn}
          disabled={isSendDisabled}
          onClick={() => sendClientTermsAndCondition({ clientId: client.id })}>
          SEND LINK TO CUSTOMER
          <i className={cn('material-icons', styles.linkIcon)}>link</i>
        </button>
      </div>

      {currentAgreement
        ? <AgreementRow agreement={currentAgreement} />
        : (
          <div className={styles.emptyState}>
            <h4 className='text-center'>No Terms and Condition Agreement</h4>
          </div>
          )}
      {history.length > 0 && (
        <>
          {showHistory && (
            <>
              <div className={styles.historyLabel}>History</div>
              {history.map((item, idx) => (
                <AgreementRow key={item.id || idx} agreement={item} showDivider />
              ))}
            </>
          )}

          <div className={styles.historyToggleRow}>
            <button
              type='button'
              className={styles.historyToggleBtn}
              onClick={() => setShowHistory(!showHistory)}>
              <span>{showHistory ? 'Hide History' : 'View History'}</span>
              <i className='material-icons'>
                {showHistory ? 'keyboard_double_arrow_up' : 'keyboard_double_arrow_down'}
              </i>
            </button>
          </div>
        </>
      )}
    </div>
  )
}

TCAgreementDisplay.propTypes = {
  client: PropTypes.object.isRequired,
  refetchClient: PropTypes.func.isRequired
}
